import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchTrackRecordSummary,
  requestTrackRecordEmail,
  SUCCESS_MESSAGE,
  TrackRecordSummary,
} from '../services/trackRecordService';
import './TrackRecordTeaser.css';

type TrackRecordTeaserProps = {
  variant?: 'landing' | 'page';
  source?: string;
};

const TrackRecordTeaser: React.FC<TrackRecordTeaserProps> = ({ variant = 'landing', source }) => {
  const [summary, setSummary] = useState<TrackRecordSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchTrackRecordSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) setSummaryError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSuccessMessage(null);
    setSubmitting(true);
    try {
      const result = await requestTrackRecordEmail(email.trim(), source);
      setSuccessMessage(result.message || SUCCESS_MESSAGE);
      setEmail('');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const asOfLabel = summary?.asOf
    ? new Date(summary.asOf).toLocaleString('en-GB', { timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className={`track-record-teaser track-record-teaser--${variant}`} id={variant === 'landing' ? 'track-record' : undefined}>
      <h3 className="track-record-teaser-title">See our full Results History</h3>
      <p className="track-record-teaser-subtitle">
        Full disclosure of our historical picks and results. Past performance is not indicative of future results.
      </p>

      {summary && !summaryError && (
        <div className="track-record-stats" aria-label="Track record summary">
          <div className="track-record-stat">
            <span className="track-record-stat-value">{summary.totalRaces}</span>
            <span className="track-record-stat-label">Settled picks</span>
          </div>
          <div className="track-record-stat">
            <span className="track-record-stat-value">{summary.winRate.toFixed(1)}%</span>
            <span className="track-record-stat-label">Win rate</span>
          </div>
          <div className="track-record-stat">
            <span className={`track-record-stat-value ${summary.totalPL >= 0 ? 'positive' : 'negative'}`}>
              {summary.totalPL >= 0 ? '+' : ''}
              {summary.totalPL.toFixed(2)}
            </span>
            <span className="track-record-stat-label">P/L (pts)</span>
          </div>
          <div className="track-record-stat">
            <span className={`track-record-stat-value ${summary.roi >= 0 ? 'positive' : 'negative'}`}>
              {summary.roi >= 0 ? '+' : ''}
              {summary.roi.toFixed(1)}%
            </span>
            <span className="track-record-stat-label">ROI</span>
          </div>
        </div>
      )}
      {summaryError && (
        <p className="track-record-teaser-muted">Summary stats are temporarily unavailable.</p>
      )}
      {asOfLabel && <p className="track-record-teaser-muted">Last updated {asOfLabel} (London)</p>}

      <form className="track-record-form" onSubmit={handleSubmit}>
        <label htmlFor={variant === 'landing' ? 'track-record-email' : 'track-record-email-page'} className="sr-only">
          Email address
        </label>
        <div className="track-record-form-row">
          <input
            id={variant === 'landing' ? 'track-record-email' : 'track-record-email-page'}
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting || !!successMessage}
          />
          <button type="submit" disabled={submitting || !!successMessage}>
            {submitting ? 'Sending…' : 'Email me Results History'}
          </button>
        </div>
        <p className="track-record-form-hint">
          We&apos;ll email your Results History link. Check spam/junk if it doesn&apos;t arrive within a few minutes.
        </p>
        {submitError && <p className="track-record-form-error">{submitError}</p>}
        {successMessage && <p className="track-record-form-success">{successMessage}</p>}
      </form>

      {variant === 'page' && (
        <p className="track-record-teaser-footer">
          Already have a link? Open it from your email, or{' '}
          <Link to="/start-checkout/betfair">start your free trial</Link> for live picks.
        </p>
      )}
    </div>
  );
};

export default TrackRecordTeaser;
